import { apiCall } from 'app/shared/lib/essette-api';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { autoinject, bindable, TaskQueue } from 'aurelia-framework';
import { Toaster } from 'app/shared/lib/toaster';
import { AutomationEngineService } from 'app/ae/services/automation-engine-service';
import { CustomRule, EventOption, EventOptions, RuleActionOption, RuleConditionOption } from 'app/core/components/automations/automations-grid';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { validate } from 'app/shared/elements/ess-rule';
import { SaveCondition } from 'app/core/components/automations/save-condition';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { localSort } from 'app/shared/lib/sorting';
import { deepClone } from 'app/shared/lib/utilities';
import { I18N } from 'aurelia-i18n';
import { compareObjects } from 'app/shared/lib/compare-objects';
import * as AE from 'app/ae/models/essette-api';

export interface RuleCondition {
    /** The RuleConditionOption associated with this condition */
    option: RuleConditionOption;
    /** Currently selected options associated with this condition */
    optionSelections: any[];
    display?: string;
    tooltipDesc?: string;
}

interface OptionSelection {
    display: string;
    children: {
        display: string;
        isActive: boolean;
    }[];
    isActive: boolean;
}

export interface RecordType {
    $display: string;
    value: string;
}

@autoinject()
export class SaveAutomation extends ComponentBase implements IForm {
    ////****************** Fields and Properties ************************************////
    @bindable
    protected automation?: CustomRule;

    @bindable
    protected moduleCode: string | null = null;

    @bindable
    protected recordTypes: RecordType[] = [];
    protected selectedRecordType?: RecordType;

    @bindable
    protected actions: RuleActionOption[] = [];
    protected selectedAction?: RuleActionOption;
    protected unsavedSelectedActionOption?: any = null;

    @bindable
    protected options: RuleConditionOption[] = [];

    @bindable
    private automations: CustomRule[] = [];

    @bindable
    protected isAdd: boolean = false;

    public conditions: RuleCondition[] = [];
    // When false, the Save Condition modal's form validation will not be factored into the Save Automation modal's validation
    protected saveConditionModalOpened: boolean = false;

    private sorting = new Sorting();
    protected isValid: boolean = false;
    protected validate = validate;

    private unsavedAutomation?: CustomRule;

    public events: EventOption[] = [EventOptions.Insert, EventOptions.Update];

    protected optionsRemaining = true;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AutomationEngineService,
        private readonly toaster: Toaster,
        private readonly taskQueue: TaskQueue,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    ////****************** Element References ***************************************////
    protected $modal!: IPopup;
    protected $form!: IForm;
    protected $saveCondition!: SaveCondition;

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        let hasChanges =
            !compareObjects(this.automation, this.unsavedAutomation, {}).areEqual
            || !compareObjects(this.selectedAction?.selectedOption, this.unsavedSelectedActionOption, {}).areEqual;

        return hasChanges;
    }

    public async submit(): Promise<boolean> {
        const response = await this.saveAutomation();
        return response;
    }

    ////****************** Events ***************************************************////
    protected automationChanged(): void {
        if(this.recordTypes.length === 1) this.selectedRecordType = this.recordTypes[0];

        this.refreshOptions();

        if(this.actions.length === 1) this.selectedAction = this.actions[0];

        // preserve unsaved changes
        if(!this.unsavedAutomation?.identifier) this.unsavedAutomation = this.automation ? deepClone(this.automation) : undefined;

        this.restoreSelectedActionOption();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        const defaultSort = new Sorting();
        defaultSort.add({
            reference: 'option.translatedDisplay',
            direction: SortDirection.Ascending
        });
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(this.conditions, this.sorting.entries.length ? sorting : defaultSort);
        event.stopPropagation();
    }

    protected handleConditionSaved(event: FormSuccessCustomEvent) {
        if(!this.automation) this.automation = {};
        this.automation.expression = this.options.filter(o => o.expression.length).map(o => o.expression).join(' && ');
        this.refreshOptions();

        this.closeSaveConditionModal();

        // stop propagation so the Save Automation modal does not close with the Save Condition modal
        event.stopPropagation();
    }

    protected eventChanged(): void {
        if(this.automation?.event) {
            const filteredConditions = this.conditions.filter(c => c.option.excludeOnEvent === this.automation?.event);
            filteredConditions.forEach(condition => this.deleteCondition(condition, true));
        }
        this.checkOptionsRemaining();
    }

    ////****************** Functions ***********************************************////
    // for success action option to be valid it must be active and have a defined value in its payloadLevel2 (i.e., code)
    protected isOptionValid: (option: any) => boolean = (option: any) => {
        const { isActive, payloadLevel2 } = option;
        return isActive && !!option[payloadLevel2];
    }

    protected checkOptionsRemaining(): void {
        this.optionsRemaining = this.conditions.length < this.options.filter(o => !o.excludeOnEvent || o.excludeOnEvent !== this.automation?.event).length;
    }

    public reset(): void {
        this.unsavedAutomation = undefined;
        this.unsavedSelectedActionOption = null;
        this.actions.forEach(a => a.selectedOption = null);
        this.conditions.splice(0);
        this.options.forEach(option => option.expression = '');
    }

    protected refreshOptions(): void {
        this.options.sort((a, b) => a.translatedDisplay.localeCompare(b.translatedDisplay));
        this.conditions.splice(0);
        this.options.forEach(option => {
            option.parsedExpression = this.generateParsedExpression(option);
            const condition = this.generateCondition(option);
            if(condition) this.conditions.push(deepClone(condition));
        });
        this.checkOptionsRemaining();
    }

    protected openSaveConditionModal(): void {
        this.saveConditionModalOpened = true;
        // use task queue to give modal time to render
        this.taskQueue.queueTask(() => this.$modal.open());
    }

    protected closeSaveConditionModal(): void {
        this.$modal.close();
        if(this.$form.reset) this.$form.reset();
        this.saveConditionModalOpened = false;
    }

    protected saveCondition(condition?: RuleCondition): void {
        this.openSaveConditionModal();
        // use task queue to give modal time to render
        this.taskQueue.queueTask(() => {
            this.$saveCondition.condition = condition || null;
        });
    }

    protected async deleteCondition(condition: RuleCondition, skipConfirmation: boolean = false): Promise<void> {
        if(!condition) return;

        if(!skipConfirmation) {
            const result = await confirmation.showDelete('ae:automation.condition.$', 1);
            if(result.dialogResponse !== confirmation.options.OK) return;
        }

        this.conditions = this.conditions.filter(c => c !== condition);
        const foundConditionOption = this.options.find(o => o.translatedDisplay === condition.option.translatedDisplay && o.$used);
        if(foundConditionOption) {
            foundConditionOption.$used = false;
            foundConditionOption.expression = '';
            foundConditionOption.parsedExpression = null;
            foundConditionOption.options.forEach(o => o.$selected = false);
            if(!this.automation) this.automation = {};
            this.automation.expression = this.options.filter(o => o.expression.length).map(o => o.expression).join(' && ');
        }
        const sorting = new Sorting();
        this.checkOptionsRemaining();
        localSort(this.options, sorting);
    }

    private constructSaveObject(automation: CustomRule, action: RuleActionOption): AE.RuleSet {
        const { event, ruleSetIdentifier, hasWarnings, warnings, ...updatedAutomation } = automation;
        const { payloadLevel1, payloadLevel2, selectedOption, successActionType } = action;
        const ruleSet: AE.RuleSet = {
            identifier: `${this.moduleCode}.${this.selectedRecordType?.value}.${event}`,
            rules: [
                {
                    ...updatedAutomation,
                    expression: automation.expression,
                    isDraft: updatedAutomation.isDraft || false,
                    successActions: [{
                        type: successActionType,
                        payload: {
                            [payloadLevel1]: [
                                {
                                    [payloadLevel2]: selectedOption[payloadLevel2]
                                }
                            ]
                        }
                    }]
                }
            ]
        };

        // add necessary includes to settings object
        const includes: string[] = [];
        this.conditions.forEach(condition => {
            if(condition.option.optionLevel1) includes.push(condition.option.optionLevel1);
            if(condition.option.subOptionLevel1) includes.push(condition.option.subOptionLevel1);
        });
        if(includes.length && ruleSet.rules?.length) {
            ruleSet.rules[0].settings = { includes: includes };
        }

        return ruleSet;
    }

    /* Parses using regular expressions a basic Raven-style string expression into an object
      Examples:
        Simple:
            Input: '(new[] { "N8", "LOW" }.Any(x => root.acuity.code == x))'
            Output: { acuity: { code: { N8: true, LOW: true } } }
        Compound:
            Input: '(new[] { "N8", "LOW" }.Any(x => root.acuity.code == x)) || (new[] { "CLOSED" }.Any(x => root.status.code == x))'
            Output: { acuity: { code: { N8: true, LOW: true } }, status: { code: { CLOSED: true } } }
        Nested:
            Input: '(program.group.code eq "ADH" && new[] { "RT1", "A" }.Any(x => root.program.code == x)))'
            Output: { program.group: { code: { RP1: { programs: { RT1: true, A: true } } } } }
    */
    protected generateParsedExpression(option: RuleConditionOption): void {
        const parsedExpression: any = {};
        const { optionLevel1, optionLevel2 } = option;
        if(!optionLevel1 || !optionLevel2) return;
        const { subOptionLevel1, subOptionLevel2, subOptionProperty } = option;
        // basic algo
        if(!subOptionProperty) {
            const re = new RegExp(`\\(new\\[\\] \\{[^}]+}\\.Any\\(x => root\\.${optionLevel1}\\.${optionLevel2} == x\\)\\)`);
            const match = this.automation?.expression?.match(re);
            if(match) {
                option.expression = match[0];
                const re = new RegExp(`"([^"]*)"`, "g");
                const matches = option.expression.match(re);
                if(!matches) return;
                matches.forEach(match => {
                    const key1 = optionLevel1;
                    const key2 = optionLevel2;
                    const key3 = match.replace(/"/g, '');
                    if(!key1 || !key2 || !key3) return;
                    if(!parsedExpression[key1]) parsedExpression[key1] = {};
                    if(!parsedExpression[key1][key2]) parsedExpression[key1][key2] = {};
                    if(!parsedExpression[key1][key2][key3]) parsedExpression[key1][key2][key3] = true;
                });
            }
        } else {
            // otherwise use nested algo
            const re = new RegExp(`\\(${optionLevel1}\\.${optionLevel2} eq \\"[^"]+" && new\\[\\] \\{[^}]+}\\.Any\\(x => root\\.${subOptionLevel1}\\.${subOptionLevel2} == x\\)\\)`, "g");
            let matches = this.automation?.expression?.match(re);
            if(!matches) return;
            option.expression = `${matches.map(m => `${m}`).join(' || ')}`;
            // if multiple matches (e.g. multiple program groups and programs), wrap all in parentheses
            if(matches.length > 1) option.expression = `(${option.expression})`;
            // for each option and suboption grouping
            matches.forEach(m => {
                const re = new RegExp(`${optionLevel1}\\.${optionLevel2} eq "([^"]+)"`);
                const match = m.match(re);
                if(!match) return;
                const key1 = optionLevel1;
                const key2 = optionLevel2;
                const key3 = match[1];
                if(!key1 || !key2 || !key3) return;
                if(!parsedExpression[key1]) parsedExpression[key1] = {};
                if(!parsedExpression[key1][key2]) parsedExpression[key1][key2] = {};
                if(!parsedExpression[key1][key2][key3]) parsedExpression[key1][key2][key3] = {};
                const subOptionRe = new RegExp(`new\\[\\] \\{[^}]+}\\.Any\\(x => root\\.${subOptionLevel1}\\.${subOptionLevel2} == x\\)\\)`);
                const subOptionMatch = m.match(subOptionRe);
                if(subOptionMatch) {
                    const re = new RegExp(`"([^"]*)"`, "g");
                    const matches = option.expression.match(re);
                    if(!matches) return;
                    matches.forEach(match => {
                        const key4 = match.replace(/"/g, '');
                        if(!key1 || !key2 || !key3 || !key4) return;
                        if(!parsedExpression[key1][key2][key3][subOptionProperty!]) parsedExpression[key1][key2][key3][subOptionProperty!] = {};
                        parsedExpression[key1][key2][key3][subOptionProperty!][key4] = true;
                    });
                }
            });
        }
        return parsedExpression;
    }

    // Converts the expression-turned-object into Conditions for display on the Conditions grid
    protected generateCondition(option: RuleConditionOption): RuleCondition | null {
        option.$used = false; // default option to Unused
        const { optionLevel1, optionLevel2, subOptionLevel1, subOptionLevel2, parsedExpression } = option;
        if(!parsedExpression || !parsedExpression.hasOwnProperty(optionLevel1)) return null;
        if(!parsedExpression[optionLevel1].hasOwnProperty(optionLevel2)) return null;
        const optionSelections = parsedExpression[optionLevel1][optionLevel2];
        const condition: RuleCondition = {
            option,
            display: '',
            optionSelections
        };

        let selectedOptionsCount = 0;
        let selectedOptions: OptionSelection[] = [];
        let selectedSubOptionsCount = 0;
        if(optionSelections) {
            // reset option selections
            option.options.forEach((o: any) => {
                o.$selected = false;
                o.$expanded = false;
            });
            Object.keys(optionSelections).forEach(os => {
                const foundSelection: any = option.options.find((o: any) => o[optionLevel2] === os);
                if(!foundSelection) return;
                foundSelection.$selected = true;
                const selectedOption: OptionSelection = { display: foundSelection.description, children: [], isActive: foundSelection.isActive };
                selectedOptionsCount++;
                if(option.subOptionProperty && subOptionLevel1 && subOptionLevel2) {
                    // reset subOption selections
                    foundSelection[option.subOptionProperty!].forEach((o: any) => o.$selected = false);
                    const subOptionSelections = optionSelections[os][option.subOptionProperty];
                    Object.keys(subOptionSelections).forEach(sos => {
                        const foundSubSelection: any = foundSelection[option.subOptionProperty!].find((o: any) => o[subOptionLevel2] === sos);
                        if(foundSubSelection) {
                            selectedOption.children.push({ display: foundSubSelection.description, isActive: foundSubSelection.isActive });
                            selectedSubOptionsCount++;
                            foundSubSelection.$selected = true;
                        }
                    });
                }
                selectedOptions.push(selectedOption);
            })
        }

        if(selectedOptions.length === 1) {
            condition.display = `${!selectedOptions[0].isActive ? '<em>' : ''}${selectedOptions[0].display}${!selectedOptions[0].isActive ? ` (${this.i18n.tr('ui:labels.inactive')})</em>` : ''}`;
            if(selectedSubOptionsCount === 1) {
                condition.display += `, ${!selectedOptions[0].children[0].isActive ? '<em>' : ''}${selectedOptions[0].children[0].display}${!selectedOptions[0].children[0].isActive ? ` (${this.i18n.tr('ui:labels.inactive')})</em>` : ''}`;
            } else if(selectedSubOptionsCount > 1) {
                condition.display += `, ${selectedSubOptionsCount} ${this.i18n.tr(option.subOptionKey, { count: selectedSubOptionsCount })}`;
                condition.tooltipDesc = this.buildConditionTooltip(option, selectedOptions, selectedSubOptionsCount);
            }
        } else {
            condition.display = `${selectedOptionsCount} ${this.i18n.tr(option.optionKey, { count: selectedOptionsCount })}`;
            if(selectedSubOptionsCount > 0) {
                condition.display += `, ${selectedSubOptionsCount} ${this.i18n.tr(option.subOptionKey, { count: selectedSubOptionsCount })}`;
            }

            condition.tooltipDesc = this.buildConditionTooltip(option, selectedOptions, selectedSubOptionsCount);
        }

        if(selectedOptionsCount) option.$used = true; // selections made, therefore option is Used and should not appear in the dropdown
        return condition;
    }

    protected buildConditionTooltip(option: RuleConditionOption, selections: OptionSelection[], subOptionsCount?: number): string {
        let listDisplay = `<header>${this.i18n.tr(option.optionKey, { count: selections.length })}`;
        if(option.subOptionKey) listDisplay += `${this.i18n.tr('ui:lists.conjunctions.and')}${this.i18n.tr(option.subOptionKey, { count: subOptionsCount })}`;
        listDisplay += `</header>`;
        selections.sort((a: any, b: any) => a.display.localeCompare(b.display)).forEach(s => {
            listDisplay += `<ul><li>${!s.isActive ? '<em>' : ''}${s.display}${!s.isActive ? ` (${this.i18n.tr('ui:labels.inactive')})</em>` : ''}`;
            if(s.children.length) {
                listDisplay += `<ul>`;
                s.children.sort((a, b) => a.display.localeCompare(b.display)).forEach(c => listDisplay += `<li>${!c.isActive ? '<em>' : ''}${c.display}${!c.isActive ? (` (${this.i18n.tr('ui:labels.inactive')})</em>`) : ''}</li>`);
                listDisplay += `</ul>`;
            }
            listDisplay += `</li></ul>`;
        });
        return listDisplay;
    }

    protected restoreSelectedActionOption(): void {
        if(this.selectedAction?.selectedOption) return;
        const foundSuccessAction = this.automation?.successActions?.find(action => action.type === this.selectedAction?.successActionType);
        if(this.selectedAction) {
            let { payloadLevel1, payloadLevel2, options } = this.selectedAction
            if(!foundSuccessAction) this.selectedAction.selectedOption = null;
            else {
                const selectedValue = foundSuccessAction.payload[payloadLevel1][0][payloadLevel2];
                if(!selectedValue) return;
                const foundActionOption = options.find(o => o[payloadLevel2] === selectedValue);
                if(!foundActionOption) return;
                this.selectedAction.selectedOption = foundActionOption;
                this.unsavedSelectedActionOption = deepClone(foundActionOption);
            }
        }
    }

    protected getSelectActiveMessage(optionKey: string): string {
        const message = this.i18n.tr('ui:actions.selectActive', { what: this.i18n.tr(optionKey).toLowerCase() });
        return message;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async saveAutomation(): Promise<boolean> {
        if(!this.automation || !this.selectedAction) return false;

        let isSuccessful = false;
        let duplicateCodeError = false;
        try {
            this.state.beginSave();

            const saveAutomation = this.constructSaveObject(this.automation, this.selectedAction);
            // check if new Identifier is already in use by any existing rules
            const foundExistingAutomation = this.automations.find(a => a.identifier === saveAutomation.rules![0].identifier);
            if(foundExistingAutomation && this.isAdd) {
                duplicateCodeError = true;
            } else {
                const response = await this.service.saveRuleSet(saveAutomation);
                isSuccessful = response.isSuccessful;
            }
        } catch(error) {
            throw error;
        } finally {
            if(duplicateCodeError) {
                this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.save_error', { what: this.i18n.tr('ae:automation.identifier.$'), item: this.i18n.tr('ae:automation.$') })).show();
            } else {
                this.toaster.standard.saved('ae:automation.$', isSuccessful);
            }
            this.state.endSave();
            return isSuccessful;
        }
    }
}

// Validates the proper configuration of a RuleConditionOption
export function checkRuleConditionOptionConfiguration(option: RuleConditionOption): void {
    const { translatedDisplay, optionKey, subOptionKey, subOptionLevel1, subOptionLevel2, subOptionProperty } = option;

    if(subOptionLevel1 || subOptionLevel2 || subOptionProperty) {
        if(!subOptionLevel1 && !subOptionLevel2 && !subOptionProperty) throw new Error('If any of the following properties are set, all must be set: subOptionLevel1, subOptionLevel2, subOptionProperty.');
    }

    if(subOptionProperty && option.options[0] && !option.options[0].hasOwnProperty(subOptionProperty)) {
        throw new Error(`Specified subOptionProperty of '${subOptionProperty}' not found in first option. Add '${subOptionProperty}' to the list of includes of the API call that loaded these options.`);
    }

    const pattern = /^[^:]+:/;
    if(optionKey && !pattern.test(optionKey)) throw new Error(`optionKey must be an I18N resource (i.e., 'um:auth.$') but is currently ${optionKey}`);
    if(subOptionKey && !pattern.test(subOptionKey)) throw new Error(`subOptionKey must be an I18N resource (i.e., 'um:auth.$') but is currently ${subOptionKey}`);
    if(translatedDisplay && pattern.test(translatedDisplay)) throw new Error(`translatedDisplay must be a translated I18N resource (i.e., 'Auth'), but is currently a raw I18N key ${translatedDisplay}`);
}

// Validates the proper configuration of a RuleActionOption
export function checkRuleActionOptionConfiguration(option: RuleActionOption): void {
    const { translatedDisplay, optionKey, options, payloadLevel2 } = option;

    const pattern = /^[^:]+:/;
    if(optionKey && !pattern.test(optionKey)) throw new Error(`optionKey must be an I18N resource (i.e., 'um:auth.$') but is currently ${optionKey}`);
    if(translatedDisplay && pattern.test(translatedDisplay)) throw new Error(`translatedDisplay must be a translated I18N resource (i.e., 'Auth'), but is currently a raw I18N key ${translatedDisplay}`);
    if(options.some(o => o.payloadLevel2 !== payloadLevel2)) throw new Error(`Each action option must have a payloadLevel2 property with a value matching the action's payloadLevel2 value of ${payloadLevel2}`);
}
